<script setup>
import { cva } from "class-variance-authority";
import { computed, useSlots } from "vue";
import { B2BBadgeType, Size, BadgeColor } from "../../../interfaces/UI";
const props = defineProps({
  color: {
    type: String,
    default: BadgeColor.GRAY
  },
  size: {
    type: String,
    default: Size.MD
  },
  type: {
    type: String,
    default: B2BBadgeType.PILL
  },
  indicator: {
    type: String,
    default: null
  },
  customTextSize: {
    type: String,
    default: void 0
  }
});
const { iconLeft, iconRight } = useSlots();
const customStyles = computed(
  () => cva("flex items-center justify-center gap-1.5 border w-max", {
    variants: {
      type: {
        badge: "badge rounded-lg font-medium",
        pill: "pill rounded-2xl font-medium",
        resource: "resource rounded-2xl pr-2 gap-2"
      },
      color: {
        gray: "text-slate-600 border-slate-200 bg-slate-50",
        purple: "text-purple-600 border-purple-200 bg-purple-50",
        turquoise: "text-turquoise-600 border-turquoise-200 bg-turquoise-50",
        teal: "text-teal-600 border-teal-200 bg-teal-50",
        cyan: "text-cyan-600 border-cyan-200 bg-cyan-50",
        blue: "text-blue-600 border-blue-200 bg-blue-50",
        yellow: "text-yellow-600 border-yellow-200 bg-yellow-50",
        orange: "text-orange-600 border-orange-200 bg-orange-50",
        rose: "text-rose-600 border-rose-200 bg-rose-50",
        pink: "text-pink-600 border-pink-200 bg-pink-50",
        modern: "text-slate-800 border-slate-300 bg-white",
        primary: "text-primary-600 border-primary-200 bg-primary-50"
      },
      size: {
        sm: "text-xs py-0.5 px-1.5 max-h-[24px]",
        md: "text-sm py-0.5 px-2 max-h-[25px]",
        lg: "text-sm py-1 px-2.5 max-h-[29px]"
      },
      icons: {
        left: "",
        right: "",
        undefined: ""
      }
    },
    compoundVariants: [
      {
        type: "resource",
        size: "sm",
        class: ""
      },
      {
        type: "resource",
        size: "md"
      },
      {
        type: "resource",
        size: "lg"
      },
      {
        type: "resource",
        size: "sm",
        icons: "left",
        class: "pl-px !py-px"
      },
      {
        type: "resource",
        size: "md",
        icons: "left",
        class: "pl-px !py-px"
      },
      {
        type: "resource",
        size: "lg",
        icons: "left",
        class: "!pl-1 !py-1"
      },
      {
        type: "resource",
        size: "sm",
        icons: "right",
        class: "pr-px !py-px"
      },
      {
        type: "resource",
        size: "md",
        icons: "right",
        class: "pr-px !py-px"
      },
      {
        type: "resource",
        size: "lg",
        icons: "right",
        class: "!pr-0.5 !py-0.5"
      },
      {
        size: "sm",
        icons: "left",
        class: "pl-1"
      },
      {
        size: "md",
        icons: "left",
        class: "pl-1"
      },
      {
        size: "lg",
        icons: "left",
        class: "pl-1"
      },
      {
        size: "sm",
        icons: "right",
        class: "pr-px"
      },
      {
        size: "md",
        icons: "right",
        class: "pr-0.5"
      },
      {
        size: "lg",
        icons: "right",
        class: "pr-1"
      }
    ]
  })({
    size: props.size,
    type: props.type,
    color: props.color,
    icons: iconPlacement.value
  })
);
const iconPlacement = computed(() => {
  if (iconLeft) return "left";
  if (iconRight) return "right";
  return void 0;
});
const customIconStyles = computed(
  () => cva("", {
    variants: {
      type: {
        badge: "",
        pill: "",
        resource: ""
      },
      size: {
        sm: "size-[18px]",
        md: "size-[21px]",
        lg: "size-[21px]"
      }
    },
    compoundVariants: [
      {
        type: "resource",
        size: "sm",
        class: "bg-primary-600 rounded-full text-white p-1"
      },
      {
        type: "resource",
        size: "md",
        class: "bg-primary-600 rounded-full text-white p-1"
      },
      {
        type: "resource",
        size: "lg",
        class: "bg-primary-600 rounded-full text-white p-1"
      }
    ]
  })({ type: props.type, size: props.size })
);
</script>

<template>
    <div :class="customStyles">
        <div
            class="flex items-center justify-center !size-[8px]"
            v-if="indicator"
        >
            <div
                is-solid
                class="rounded-full size-[6px]"
                :class="`bg-${indicator}-600`"
            />
        </div>
        <div v-if="$slots.iconLeft" :class="customIconStyles">
            <slot name="iconLeft" :class="customIconStyles" />
        </div>
        <slot />
        <div v-if="$slots.iconRight" :class="customIconStyles">
            <slot name="iconRight" />
        </div>
    </div>
</template>

<style scoped>
.badge :deep(svg),
.pill :deep(svg) {
  width: inherit;
  height: inherit;
}
</style>
